import React, { useState } from 'react';
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import { createPreRegistration } from 'api/preRegistration';
import { motion } from 'framer-motion';

export default function CTASection() {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setError(null);
    try {
      // Success is claimed only after the request is recorded server-side.
      await createPreRegistration({ email, company, source: 'cta' });
      setIsSubmitted(true);
    } catch (err) {
      setError(
        err?.response?.status === 429
          ? 'Слишком много запросов. Попробуйте позже.'
          : 'Не удалось отправить заявку. Попробуйте ещё раз.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-black py-24 relative overflow-hidden">
      {/* Decorative shapes */}
      <motion.div
        className="absolute top-10 left-10 w-24 h-24 bg-[#d4ff00] border-4 border-[#d4ff00]"
        animate={{ rotate: [0, 10, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-10 right-10 w-32 h-32 border-4 border-[#ff6b35] rounded-full"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 right-1/4 w-16 h-16 bg-[#4361ee] border-4 border-[#4361ee] rotate-45"
        animate={{ rotate: [45, 90, 45] }}
        transition={{ duration: 5, repeat: Infinity }}
      />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 bg-[#d4ff00] text-black px-4 py-2 border-4 border-[#d4ff00] mb-8">
            <Sparkles className="w-4 h-4" />
            <span className="font-bold text-sm uppercase tracking-wider">Ограниченный ранний доступ</span>
          </div>

          <h2 className="text-[clamp(1.5rem,8.6vw,2.25rem)] md:text-6xl font-black text-white mb-6">
            Готовы перестать
            <br />
            <span className="text-[#d4ff00]">возиться с данными?</span>
          </h2>

          <p className="text-xl text-white/70 font-medium mb-12 max-w-2xl mx-auto">
            Оставьте заявку и одними из первых получите полностью автоматическую аналитику.
            Участникам раннего доступа — приоритетный онбординг и специальные условия.
          </p>

          {!isSubmitted ? (
            <motion.form
              onSubmit={handleSubmit}
              className="bg-white border-4 border-white p-6 md:p-8 shadow-[8px_8px_0_0_#d4ff00] max-w-xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="space-y-5">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-14 text-lg bg-[#f5f5f0] border-4 border-black rounded-none focus:ring-0 focus:border-black placeholder:text-black/40"
                />
                <Input
                  type="text"
                  placeholder="Компания (необязательно)"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="h-14 text-lg bg-[#f5f5f0] border-4 border-black rounded-none focus:ring-0 focus:border-black placeholder:text-black/40"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 text-lg font-bold bg-black text-[#d4ff00] border-4 border-black rounded-none shadow-[4px_4px_0_0_#d4ff00] hover:shadow-[6px_6px_0_0_#d4ff00] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:bg-[#1a1a1a] transition-all disabled:opacity-50"
                >
                  {isSubmitting ? 'Отправляем…' : 'Оставить заявку'}
                  <ArrowRight className="w-5 h-5" />
                </Button>
                {error && (
                  <p className="font-bold text-red-700" role="alert">
                    {error}
                  </p>
                )}
              </div>
            </motion.form>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#d4ff00] border-4 border-[#d4ff00] p-8 max-w-xl mx-auto"
            >
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 bg-black flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#d4ff00]" />
                </div>
                <div className="text-left">
                  <div className="font-black text-xl text-black">Вы в списке!</div>
                  <div className="text-black/70 font-medium">Скоро пришлём приглашение в ранний доступ.</div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
